import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  DEFAULT_HOMEPAGE_CONTENT,
  HOMEPAGE_PREVIEW_STORAGE_KEY,
  HomepageContent,
  HomepageDifficulty,
  HomepageFeature,
} from '../types/homepageContent';
import './HomepageEditor.css';

const API_BASE = process.env.REACT_APP_API_URL || '';
const SESSION_KEY = 'hitster_admin_pin';

const HomepageEditor = () => {
  const navigate = useNavigate();
  const [pin, setPin] = useState(sessionStorage.getItem(SESSION_KEY) || '');
  const [pinInput, setPinInput] = useState('');
  const [pinError, setPinError] = useState('');
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState('');
  const [error, setError] = useState('');
  const [content, setContent] = useState<HomepageContent>(DEFAULT_HOMEPAGE_CONTENT);
  const [uploadingIconIndex, setUploadingIconIndex] = useState<number | null>(null);

  const loadContent = async () => {
    setLoading(true);
    setError('');
    try {
      const res = await fetch(`${API_BASE}/api/homepage-content`);
      if (!res.ok) throw new Error('Kon homepage content niet laden');
      const data = (await res.json()) as HomepageContent;
      setContent(data);
    } catch {
      setError('Kon homepage content niet laden');
    }
    setLoading(false);
  };

  useEffect(() => {
    loadContent();
  }, []);

  const updateFeature = (index: number, patch: Partial<HomepageFeature>) => {
    setContent((prev) => {
      const next = [...prev.features];
      next[index] = { ...next[index], ...patch };
      return { ...prev, features: next };
    });
  };

  const addFeature = () => {
    setContent((prev) => ({
      ...prev,
      features: [
        ...prev.features,
        {
          icon: '✨',
          title: 'Nieuw onderdeel',
          description: 'Beschrijf hier deze mogelijkheid.',
          ctaText: '',
          ctaUrl: '',
        },
      ],
    }));
  };

  const removeFeature = (index: number) => {
    setContent((prev) => {
      if (prev.features.length <= 1) {
        return prev;
      }
      const next = [...prev.features];
      next.splice(index, 1);
      return { ...prev, features: next };
    });
  };

  const handleFeatureIconUpload = async (index: number, file: File | null) => {
    if (!file) return;
    setUploadingIconIndex(index);
    setError('');
    setMessage('');
    try {
      const formData = new FormData();
      formData.append('image', file);

      const res = await fetch(`${API_BASE}/api/upload`, {
        method: 'POST',
        body: formData,
      });

      if (!res.ok) {
        throw new Error('Upload mislukt');
      }

      const data = (await res.json()) as { imageUrl?: string };
      if (!data.imageUrl) {
        throw new Error('Geen upload URL ontvangen');
      }

      updateFeature(index, { imageUrl: data.imageUrl });
      setMessage('Afbeelding geupload. Vergeet niet op Opslaan te klikken.');
    } catch {
      setError('Afbeelding uploaden mislukt');
    }
    setUploadingIconIndex(null);
  };

  const updateDifficulty = (index: number, patch: Partial<HomepageDifficulty>) => {
    setContent((prev) => {
      const next = [...prev.difficulties];
      next[index] = { ...next[index], ...patch };
      return { ...prev, difficulties: next };
    });
  };

  const handlePinLogin = (e: React.FormEvent) => {
    e.preventDefault();
    setPinError('');
    setPin(pinInput);
    sessionStorage.setItem(SESSION_KEY, pinInput);
  };

  const handleSave = async () => {
    setSaving(true);
    setError('');
    setMessage('');
    try {
      const res = await fetch(`${API_BASE}/api/admin/homepage-content`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'X-Admin-Pin': pin,
        },
        body: JSON.stringify(content),
      });

      if (res.status === 401) {
        setPin('');
        sessionStorage.removeItem(SESSION_KEY);
        setPinError('Onjuiste PIN');
        setSaving(false);
        return;
      }

      if (!res.ok) {
        throw new Error('Opslaan mislukt');
      }

      const saved = (await res.json()) as HomepageContent;
      setContent(saved);
      setMessage('Homepage opgeslagen');
    } catch {
      setError('Opslaan mislukt');
    }
    setSaving(false);
  };

  const handlePreview = () => {
    localStorage.setItem(HOMEPAGE_PREVIEW_STORAGE_KEY, JSON.stringify(content));
    window.open('/?preview=1', '_blank', 'noopener,noreferrer');
  };

  if (!pin) {
    return (
      <div className="homepage-editor-page">
        <div className="homepage-editor-login-card">
          <h1>🔐 Homepage Beheer</h1>
          <p>Voer je admin PIN in om de startpagina te beheren.</p>
          <form onSubmit={handlePinLogin}>
            <input
              type="password"
              value={pinInput}
              onChange={(e) => setPinInput(e.target.value)}
              placeholder="Admin PIN"
              autoFocus
            />
            {pinError && <div className="homepage-editor-error">{pinError}</div>}
            <div className="homepage-editor-login-actions">
              <button type="button" onClick={() => navigate('/spotify-dashboard')}>
                Terug
              </button>
              <button type="submit">Inloggen</button>
            </div>
          </form>
        </div>
      </div>
    );
  }

  return (
    <div className="homepage-editor-page">
      <div className="homepage-editor-header">
        <button onClick={() => navigate('/spotify-dashboard')}>← Terug naar dashboard</button>
        <h1>🏠 Homepage Beheer</h1>
        <div className="homepage-editor-actions">
          <button
            type="button"
            className="secondary"
            onClick={handlePreview}
          >
            Preview
          </button>
          <button
            type="button"
            className="secondary"
            onClick={() => setContent(DEFAULT_HOMEPAGE_CONTENT)}
          >
            Reset naar standaard
          </button>
          <button type="button" onClick={handleSave} disabled={saving || loading}>
            {saving ? 'Opslaan...' : 'Opslaan'}
          </button>
        </div>
      </div>

      {loading ? <p>Laden...</p> : null}
      {message ? <div className="homepage-editor-success">{message}</div> : null}
      {error ? <div className="homepage-editor-error">{error}</div> : null}

      <div className="homepage-editor-grid">
        <section>
          <h2>Hero</h2>
          <label>Titel</label>
          <input
            value={content.heroTitle}
            onChange={(e) => setContent((prev) => ({ ...prev, heroTitle: e.target.value }))}
          />
          <label>Tagline</label>
          <input
            value={content.tagline}
            onChange={(e) => setContent((prev) => ({ ...prev, tagline: e.target.value }))}
          />
        </section>

        <section>
          <h2>Intro</h2>
          <label>Titel</label>
          <input
            value={content.introTitle}
            onChange={(e) => setContent((prev) => ({ ...prev, introTitle: e.target.value }))}
          />
          <label>Tekst</label>
          <textarea
            rows={5}
            value={content.introText}
            onChange={(e) => setContent((prev) => ({ ...prev, introText: e.target.value }))}
          />
        </section>

        <section>
          <h2>Mogelijkheden Cards</h2>
          <label>Sectietitel</label>
          <input
            value={content.featuresTitle}
            onChange={(e) => setContent((prev) => ({ ...prev, featuresTitle: e.target.value }))}
          />
          <button type="button" className="homepage-editor-add-card-btn" onClick={addFeature}>
            + Card toevoegen
          </button>
          {content.features.map((feature, index) => (
            <div key={`feature-${index}`} className="editor-subcard">
              <div className="editor-subcard-header">
                <strong>Card {index + 1}</strong>
                <button
                  type="button"
                  className="homepage-editor-remove-card-btn"
                  onClick={() => removeFeature(index)}
                  disabled={content.features.length <= 1}
                >
                  Verwijderen
                </button>
              </div>
              <label>Badge / icon (emoji)</label>
              <input
                value={feature.icon}
                onChange={(e) => updateFeature(index, { icon: e.target.value })}
              />
              <label>Afbeelding URL</label>
              <input
                value={feature.imageUrl || feature.iconUrl || ''}
                onChange={(e) => updateFeature(index, { imageUrl: e.target.value })}
                placeholder="/uploads/icon.png"
              />
              <label>Upload afbeelding</label>
              <input
                type="file"
                accept="image/*"
                onChange={(e) => {
                  const selectedFile = e.target.files?.[0] || null;
                  handleFeatureIconUpload(index, selectedFile);
                  e.currentTarget.value = '';
                }}
              />
              {uploadingIconIndex === index ? <p>Uploaden...</p> : null}
              {feature.imageUrl || feature.iconUrl ? (
                <img
                  src={feature.imageUrl || feature.iconUrl}
                  alt={`Feature icon ${index + 1}`}
                  className="homepage-editor-icon-preview"
                />
              ) : null}
              <label>Titel</label>
              <input
                value={feature.title}
                onChange={(e) => updateFeature(index, { title: e.target.value })}
              />
              <label>Omschrijving</label>
              <textarea
                rows={3}
                value={feature.description}
                onChange={(e) => updateFeature(index, { description: e.target.value })}
              />
              <label>Card knoptekst (optioneel)</label>
              <input
                value={feature.ctaText || ''}
                onChange={(e) => updateFeature(index, { ctaText: e.target.value })}
                placeholder="Bijv. Lees meer"
              />
              <label>Card knoplink (optioneel)</label>
              <input
                value={feature.ctaUrl || ''}
                onChange={(e) => updateFeature(index, { ctaUrl: e.target.value })}
                placeholder="Bijv. /contact of https://..."
              />
            </div>
          ))}
        </section>

        <section>
          <h2>Moeilijkheidsgraden</h2>
          <label>Sectietitel</label>
          <input
            value={content.difficultyTitle}
            onChange={(e) => setContent((prev) => ({ ...prev, difficultyTitle: e.target.value }))}
          />
          {content.difficulties.map((difficulty, index) => (
            <div key={`difficulty-${index}`} className="editor-subcard">
              <label>Naam</label>
              <input
                value={difficulty.name}
                onChange={(e) => updateDifficulty(index, { name: e.target.value })}
              />
              <label>Omschrijving</label>
              <input
                value={difficulty.description}
                onChange={(e) => updateDifficulty(index, { description: e.target.value })}
              />
              <label>Tokens tekst</label>
              <input
                value={difficulty.tokens}
                onChange={(e) => updateDifficulty(index, { tokens: e.target.value })}
              />
            </div>
          ))}
        </section>

        <section>
          <h2>Knoppen en footer</h2>
          <label>Join titel</label>
          <input
            value={content.joinTitle}
            onChange={(e) => setContent((prev) => ({ ...prev, joinTitle: e.target.value }))}
          />
          <label>Join tekst</label>
          <input
            value={content.joinText}
            onChange={(e) => setContent((prev) => ({ ...prev, joinText: e.target.value }))}
          />
          <label>Join knoptekst</label>
          <input
            value={content.joinButtonText}
            onChange={(e) => setContent((prev) => ({ ...prev, joinButtonText: e.target.value }))}
          />
          <label>Join knoplink</label>
          <input
            value={content.joinButtonUrl || ''}
            onChange={(e) => setContent((prev) => ({ ...prev, joinButtonUrl: e.target.value }))}
            placeholder="Bijv. /join of https://..."
          />
          <label>Admin knoptekst</label>
          <input
            value={content.adminButtonText}
            onChange={(e) => setContent((prev) => ({ ...prev, adminButtonText: e.target.value }))}
          />
          <label>Footer tekst</label>
          <input
            value={content.footerText}
            onChange={(e) => setContent((prev) => ({ ...prev, footerText: e.target.value }))}
          />
        </section>
      </div>
    </div>
  );
};

export default HomepageEditor;
